import React, { useState, useEffect } from 'react';
import Offcanvas from 'react-bootstrap/Offcanvas';
import { saveAs } from 'file-saver';
import moment from 'moment';
import BuySellService from '../../../services/buy-sell.service';
import { toast } from 'react-toastify';
import Loader from '../../../shared/common/loader';
import {
  addThousandSeparators,
  formatAmount,
  getTransactionType,
} from '../../../utils';
import { useHistory } from 'react-router-dom';
import {
  BUYBACK_INVOICE_APPROVAL_MESSAGE,
  BUYBACK_INVOICE_UPLOAD_MESSAGE,
  INCOMPLETE_BUYBACK_INVOICE,
  INCOMPLETE_SELL_INVOICE,
  PAYMENT_PENDING_MESSAGE,
} from 'constants/messages.constant';
import { userStore } from 'store/UserStore';
import '../styles.css';

interface PropsType {
  handleModal: any;
  isShow: boolean;
  data: any;
  isPassbookRedirect?: boolean;
  displaySummary: boolean;
  displayInvoice: boolean;
}

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const getStatusClass = (s: string) => {
    if (['COMPLETED'].includes(s)) return 'status-badge status-success';
    if (
      ['PAYMENT_PENDING', 'CREATED', 'INVOICE_UPLOAD_PENDING',
       'INVOICE_APPROVAL_PENDING', 'BANK_TRANSFER_APPROVAL_PENDING'].includes(s)
    ) return 'status-badge status-pending';
    if (['PENDING'].includes(s)) return 'status-badge status-warning';
    return 'status-badge status-default';
  };

  const formatStatus = (s: string) =>
    s.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

  return <span className={getStatusClass(status)}>{formatStatus(status)}</span>;
};

const InfoRow: React.FC<{ label: string; value: React.ReactNode; bold?: boolean }> = ({
  label, value, bold,
}) => (
  <div className="os-info-row">
    <span className="os-info-label">{label}</span>
    <span className={`os-info-value ${bold ? 'os-info-value--bold' : ''}`}>{value}</span>
  </div>
);

const isRollup = (d: any): boolean =>
  Array.isArray(d?.orders) && d.orders.length > 0 && !d?.status;

// ─────────────────────────────────────────────────────────────────────────────
const OrderSummary: React.FC<PropsType> = ({
  handleModal,
  isShow,
  data,
  isPassbookRedirect,
  displaySummary,
  displayInvoice,
}) => {
  // ── 1. ALL hooks first — no returns before this block ────────────────────
  const [loading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState('');
  const [customer, user] = userStore((state: any) => [state.customer, state.user]);
  const history = useHistory();

  // Derive status/type early so useEffect can depend on them,
  // but guard every access with ?. so null data doesn't throw.
  const rollup: boolean = data ? isRollup(data) : false;
  const childOrders: any[] = rollup ? (data?.orders ?? []) : [];

  const displayStatus: string = rollup
    ? (childOrders[0]?.status ?? 'COMPLETED')
    : (data?.status ?? '');

  const displayType: string = rollup
    ? (childOrders[0]?.type ?? '')
    : (data?.type ?? '');

  // ── 2. useEffect — still before any conditional return ───────────────────
  useEffect(() => {
    if (!displayStatus) {
      setMessage('');
      return;
    }
    switch (displayStatus) {
      case 'PAYMENT_PENDING':
      case 'CREATED':
        setMessage(PAYMENT_PENDING_MESSAGE);
        break;
      case 'PENDING':
        setMessage(displayType === 'SELL' ? INCOMPLETE_SELL_INVOICE : INCOMPLETE_BUYBACK_INVOICE);
        break;
      case 'INVOICE_UPLOAD_PENDING':
        setMessage(BUYBACK_INVOICE_UPLOAD_MESSAGE);
        break;
      case 'INVOICE_APPROVAL_PENDING':
        setMessage(BUYBACK_INVOICE_APPROVAL_MESSAGE);
        break;
      default:
        setMessage('');
    }
  }, [displayStatus, displayType]);

  // ── 3. NOW safe to bail out ───────────────────────────────────────────────
  if (!data) return null;

  // ── 4. All remaining derivations (no hooks below this line) ──────────────
  const displayCreatedAtUnix: number = rollup
    ? (data.createdAt ? new Date(data.createdAt).getTime() : childOrders[0]?.createdAtUnix ?? 0)
    : (data.createdAtUnix ?? 0);

  const displayOrderRef: string = rollup
    ? (data.ebOrderRef ?? childOrders[0]?.ebOrderRef ?? childOrders[0]?.order?.ebOrderRef ?? '')
    : (data.ebOrderRef ?? data.order?.ebOrderRef ?? '');

  const displayPaymentMethod: string = rollup
    ? (childOrders[0]?.order?.paymentMethod ?? '')
    : (data.order?.paymentMethod ?? data.paymentMethod ?? '');

  const paymentRequest: any = rollup
    ? childOrders[0]?.meta?.payment?.request
    : data?.meta?.payment?.request;

  const splitPayMethod: string | undefined = paymentRequest?.transactionMethod;
  const walletAmount: number = Number(paymentRequest?.walletAmount ?? 0);
  const splitPayAmount: number = Number(paymentRequest?.amount ?? 0);

  const displayGstInvoiceRef: string[] = data?.gstInvoiceRef ?? [];

  const sourceItems: any[] = rollup
    ? childOrders.flatMap((o: any) => o?.items ?? [])
    : (data.items ?? []);

  const allBreakdowns: any[] = sourceItems.flatMap(
    (item: any) => item?.itemBreakDown ?? []
  );

  const isMultiMetal = allBreakdowns.length > 1;

  const metalNames = allBreakdowns
    .map((bd: any) => bd?.name ?? '')
    .filter(Boolean)
    .join(' + ');

  const allTaxes: any[] = allBreakdowns.flatMap((bd: any) => bd?.taxes ?? []);

  const consolidatedTaxes: { subTaxType: string; rate: number; value: number }[] =
    Object.values(
      allTaxes.reduce((acc: Record<string, any>, tax: any) => {
        const key = tax.subTaxType;
        if (!acc[key]) acc[key] = { subTaxType: key, rate: Number(tax.rate) || 0, value: 0 };
        acc[key].value += Number(tax.value) || 0;
        return acc;
      }, {})
    );

  const totalRounding: number = allBreakdowns.reduce(
    (acc: number, bd: any) => acc + (Number(bd?.summary?.roundingDifference) || 0),
    0
  );

  let itemTotalDiscount = 0;
  let quotationTax = 0;
  let quotationTotal = 0;

  if (rollup) {
    childOrders.forEach((o: any) => {
      itemTotalDiscount += Number(o?.summary?.itemTotalDiscount) || 0;
      quotationTax     += Number(o?.summary?.quotationTax)      || 0;
      quotationTotal   += Number(o?.summary?.quotationTotal)    || 0;
    });
  } else {
    const s = data.summary ?? {};
    itemTotalDiscount = Number(s.itemTotalDiscount) || 0;
    quotationTax      = Number(s.quotationTax)      || 0;
    quotationTotal    = Number(data.quotationTotal ?? s.quotationTotal) || 0;
  }

  const displayInvoiceTaxes: any[] = rollup
    ? childOrders.flatMap((o: any) => o?.invoiceTaxes ?? [])
    : (data.invoiceTaxes ?? []);

  const calcTotalTax = (taxes: any[]): number =>
    taxes.reduce((acc: number, curr: any) => acc + (Number(curr.value) || 0), 0);

  const onDownloadInvoice = async () => {
    if (!displayGstInvoiceRef.length) {
      toast.error('No invoices to download');
      return;
    }
    try {
      setLoading(true);
      const encryptedRef = encodeURIComponent(displayGstInvoiceRef[0]);
      const res: any = await BuySellService.downloadInvoice(encryptedRef, user?.userId);
      if (res.status === 200 && res.data) {
        saveAs(res.data.data.fileUrl, `INVOICE_${displayGstInvoiceRef[0]}`);
        window.location.assign(res.data.data.fileUrl);
      }
    } catch (error: any) {
      toast.error(
        error?.data?.errors?.[0]?.title ?? 'Something went wrong'
      );
    } finally {
      setLoading(false);
    }
  };

  // ── 5. Render ─────────────────────────────────────────────────────────────
  return (
    <>
      {loading && <Loader />}

      <Offcanvas
        show={isShow}
        onHide={() => handleModal(false)}
        placement="end"
        className="os-offcanvas"
      >
        <Offcanvas.Header closeButton className="os-header">
          <Offcanvas.Title className="os-header-inline">
            <div className="os-header-text">
              <span className="os-title">
                Your {metalNames || 'Order'}{' '}
                {displayType === 'SELL' ? 'Purchase' : 'Sales'}
              </span>
              <span className="os-subtitle">#{displayOrderRef}</span>
            </div>
          </Offcanvas.Title>
        </Offcanvas.Header>

        <Offcanvas.Body className="os-body">

          <div className="os-status-bar">
            <StatusBadge status={displayStatus} />
            <span className="os-date">
              {moment(displayCreatedAtUnix).format('DD MMM YYYY, hh:mm A')}
            </span>
          </div>

          <div className="os-card">
            <p className="os-card-label">Order Details</p>
            <InfoRow
              label="Order Number"
              value={<span className="os-mono">#{displayOrderRef}</span>}
            />
            <InfoRow
              label="Payment Mode"
              value={getTransactionType(rollup ? childOrders[0] : data)}
            />
            {splitPayMethod && walletAmount > 0 && (
              <>
                <InfoRow
                  label={splitPayMethod === 'NEBK' ? 'Net Banking' : 'UPI'}
                  value={`₹ ${formatAmount(splitPayAmount - walletAmount)}`}
                />
                <InfoRow label="Wallet" value={`₹ ${formatAmount(walletAmount)}`} />
              </>
            )}
          </div>

          {allBreakdowns.length > 0 && (
            <div className="os-card">
              <p className="os-card-label">Price Breakdown</p>
              {allBreakdowns.map((bd: any, i: number) => {
                const s = bd?.summary;
                if (!s) return null;
                return (
                  <React.Fragment key={i}>
                    {isMultiMetal && <p className="os-metal-sublabel">{bd.name}</p>}
                    <InfoRow label="Rate"     value={`₹ ${formatAmount(s.unitPrice, 2)}/g`} />
                    <InfoRow label="Quantity" value={`${Number(s.originalQuantity).toFixed(4)} g`} />
                    {Number(s.adjustedQuantity) !== 0 && (
                      <InfoRow
                        label="Product Discount"
                        value={`${Number(s.adjustedQuantity).toFixed(4)} g`}
                      />
                    )}
                    <InfoRow label="Line Amount" value={`₹ ${formatAmount(s.lineAmount)}`} />
                  </React.Fragment>
                );
              })}
              {itemTotalDiscount !== 0 && (
                <InfoRow
                  label="Discount"
                  value={<span className="os-discount">− ₹ {formatAmount(itemTotalDiscount)}</span>}
                />
              )}
            </div>
          )}

          {consolidatedTaxes.length > 0 && (
            <div className="os-card">
              <div className="os-card-label-row">
                <p className="os-card-label">Tax & Charges</p>
                <span className="os-card-total">₹ {formatAmount(calcTotalTax(allTaxes))}</span>
              </div>
              {consolidatedTaxes.map((tax, i) => (
                <div key={i} className="os-tax-row">
                  <span className="os-tax-label">{tax.subTaxType} ({tax.rate}%)</span>
                  <span className="os-tax-value">₹ {formatAmount(tax.value)}</span>
                </div>
              ))}
            </div>
          )}

          {quotationTax !== 0 && (
            <div className="os-card">
              <div className="os-card-label-row">
                <p className="os-card-label">Other Charges & Taxes</p>
                <span className="os-card-total">₹ {formatAmount(quotationTax)}</span>
              </div>
              {displayInvoiceTaxes.map((tax: any, i: number) => (
                <div key={i} className="os-tax-row">
                  <span className="os-tax-label">
                    {tax.taxSubType} ({addThousandSeparators(tax.value)}%)
                  </span>
                  <span className="os-tax-value">₹ {formatAmount(tax.amount)}</span>
                </div>
              ))}
            </div>
          )}

          {totalRounding !== 0 && (
            <div className="os-card os-card--flat">
              <InfoRow label="Rounding Difference" value={`₹ ${formatAmount(totalRounding)}`} />
            </div>
          )}

          <div className="os-total-bar">
            <span className="os-total-label">Total Amount</span>
            <span className="os-total-value">₹ {formatAmount(quotationTotal)}</span>
          </div>

          {displayStatus === 'BANK_TRANSFER_APPROVAL_PENDING' && (
            <div className="os-note">
              <span className="os-note-icon">ℹ</span>
              <span>
                Settlement takes 1–2 business days. Orders placed on Fri, Sat &amp; Sun
                settle by upcoming Monday/Tuesday.
              </span>
            </div>
          )}

          <div className="os-actions">
            {displayStatus !== 'COMPLETED' ? (
              isPassbookRedirect ? (
                <button
                  className="os-btn os-btn--primary"
                  onClick={() => history.push('/passbook')}
                >
                  View Passbook
                </button>
              ) : message ? (
                <div className="os-message">{message}</div>
              ) : null
            ) : (
              displayInvoice && displayPaymentMethod !== 'GIFT' && (
                <button className="os-btn os-btn--primary" onClick={onDownloadInvoice}>
                  <span className="os-btn-icon">↓</span> Download Invoice
                </button>
              )
            )}
          </div>

        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
};

export default OrderSummary;